import React, { useMemo } from "react";
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import { MONTH_LABELS, CHART_FONT, buildMonthlySeries } from "src/utils/chart/chartUtils";

// 순수 라인차트만 그리므로 BarElement는 등록할 필요가 없음
ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	Title,
	Tooltip,
	Legend,
);

// 선 1개를 구성하는 필드/라벨/색상 정보
export interface LineConfig<T> {
	field: keyof T;
	label: string;
	color: string;
	emphasize?: boolean; // 종합지표 등 강조가 필요한 선 (두께/포인트 크기 증가)
}

interface Props<T extends { mm: number }> {
	data: T[];
	lines: LineConfig<T>[]; // 표시할 선 목록
	yMax?: number;          // y축 최대값 (기본 120)
	height?: number;
}

// PRD5050(설비종합효율/OEE)처럼 모든 지표가 %(퍼센트) 단위인 화면이 사용하는
// 단일 y축 순수 라인차트 컴포넌트
export const MultiLineRateChart = <T extends { mm: number }>({
	data,
	lines,
	yMax = 120,
	height = 450,
}: Props<T>) => {

	const { chartData, chartOptions } = useMemo(() => {
		const chartData = {
			labels: MONTH_LABELS,
			datasets: lines.map((line) => ({
				label: line.label,
				data: buildMonthlySeries(data, line.field),
				borderColor: line.color,
				backgroundColor: line.color,
				tension: 0.3,
				pointRadius: line.emphasize ? 4 : 3,
				...(line.emphasize ? { borderWidth: 3 } : {}),
			})),
		};

		const chartOptions = {
			maintainAspectRatio: false,
			responsive: true,
			layout: {
				padding: { left: 12, right: 12, top: 8, bottom: 4 },
			},
			interaction: { mode: "index" as const, intersect: false },
			plugins: {
				legend: { position: "bottom" as const, labels: { font: CHART_FONT } },
				tooltip: { mode: "index" as const, intersect: false },
			},
			scales: {
				y: {
					type: "linear" as const,
					beginAtZero: true,
					max: yMax,
					ticks: {
						font: CHART_FONT,
						color: '#000000',
						padding: 10,
						callback: (v: number | string) => `${v}%`,
					},
				},
				x: { ticks: { font: CHART_FONT, color: '#000000' } },
			},
		};

		return { chartData, chartOptions };
	}, [data, lines, yMax]);

	return (
		<div style={{ height: `${height}px` }} className="w-full border border-gray-300 p-2">
			<Line data={chartData} options={chartOptions} />
		</div>
	);
};

export default MultiLineRateChart;
